import { chartTooltipHtml } from "../chartTooltipHtml";
import * as d3 from "d3";

const CHART_CONFIG = {
  yMin: 0,
  yMax: 100,
  leftMargin: 52,
  rightMargin: 24,
  topMargin: 12,
  bottomMargin: 24,
  colors: {
    current: "#22c55e", // Green
    previous: "#d1d5db", // Gray
    areaFill: "#dcfce7", // Light green
  },
};

const parseDate = d3.utcParse("%Y-%m-%d");

const fmtPct = (val) =>
  val === null || val === undefined || Number.isNaN(val)
    ? "-"
    : `${Math.round(val)}%`;

const fmtDateLabel = (dateStr) =>
  new Intl.DateTimeFormat("en-US", {
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(dateStr));

export function participationTrend() {
  return {
    loading: false,
    tooltip: null,
    trendDataAll: null,
    trendDataUser: null,

    async init() {
      const url = this.$el?.dataset?.url;
      if (!url) return;

      this.loading = true;
      try {
        // Fetch both datasets
        const res = await fetch(url);
        if (!res.ok) return;

        const data = await res.json();
        this.trendDataAll = data.all_teams;
        this.trendDataUser = data.your_teams;

        // Render with current tab's data
        this.renderChart();

        // Watch for tab changes
        this.$watch("$store.snapshotResultFilters.activeTab", () => {
          this.renderChart();
        });
      } finally {
        this.loading = false;
      }
    },

    destroy() {
      this.tooltip?.remove?.();
      this.tooltip = null;
    },

    // Helper to get data for the active tab
    getDataForActiveTab() {
      const activeTab =
        this.$store?.snapshotResultFilters?.activeTab || "all-teams";
      return activeTab === "your-teams"
        ? this.trendDataUser
        : this.trendDataAll;
    },

    renderChart() {
      const elChart = this.$refs.chart;
      const elToday = this.$refs.today;
      if (!elChart) return;

      // Get data for current active tab
      const data = this.getDataForActiveTab();

      if (!data?.current?.start_date || !data?.chart_end_date) return;

      // Tooltip is appended to <body>, so it won't be cleared by elChart.innerHTML.
      // Remove any prior tooltip before re-rendering to avoid orphaned DOM nodes.
      this.tooltip?.remove?.();
      this.tooltip = null;

      elChart.innerHTML = "";

      const { values, comparisonValues } = this.prepareChartData(data);

      // Get container dimensions
      const containerWidth = elChart.clientWidth || 800;
      const containerHeight = elChart.clientHeight || 256;

      // Dimensions and margins (standard D3 pattern)
      const margin = {
        top: CHART_CONFIG.topMargin,
        right: CHART_CONFIG.rightMargin,
        bottom: CHART_CONFIG.bottomMargin,
        left: CHART_CONFIG.leftMargin,
      };
      const width = containerWidth - margin.left - margin.right;
      const height = containerHeight - margin.top - margin.bottom;

      // Create SVG with margins
      const svg = d3
        .select(elChart)
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("viewBox", `0 0 ${containerWidth} ${containerHeight}`)
        .attr("preserveAspectRatio", "xMidYMid meet")
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Create scales (range from 0 to width/height after margin translation)
      // X-axis goes from start_date to chart_end_date (whichever window is longer)
      const xScale = d3
        .scaleUtc()
        .domain([
          parseDate(data.current.start_date),
          parseDate(data.chart_end_date),
        ])
        .range([0, width]);

      const yScale = d3
        .scaleLinear()
        .domain([CHART_CONFIG.yMin, CHART_CONFIG.yMax])
        .range([height, 0]);

      // Create generators
      const area = d3
        .area()
        .defined((d) => d.value !== null)
        .x((d) => xScale(parseDate(d.date)))
        .y0(height) // Baseline at bottom of chart
        .y1((d) => yScale(d.value));

      const line = d3
        .line()
        .defined((d) => d.value !== null)
        .x((d) => xScale(parseDate(d.date)))
        .y((d) => yScale(d.value));

      // Add y-axis grid lines
      svg
        .append("g")
        .attr("class", "grid")
        .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(""))
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".tick line")
            .style("stroke", "#e5e7eb")
            .style("stroke-opacity", 0.7),
        );

      // Add area fill
      svg
        .append("path")
        .datum(values)
        .attr("class", "area")
        .attr("fill", CHART_CONFIG.colors.areaFill)
        .attr("opacity", 0.5)
        .attr("d", area);

      // Add previous line (gray)
      if (comparisonValues?.length > 0) {
        svg
          .append("path")
          .datum(comparisonValues)
          .attr("class", "line-previous")
          .attr("fill", "none")
          .attr("stroke", CHART_CONFIG.colors.previous)
          .attr("stroke-width", 2)
          .attr("d", line);
      }

      // Add current line (green)
      svg
        .append("path")
        .datum(values)
        .attr("class", "line-current")
        .attr("fill", "none")
        .attr("stroke", CHART_CONFIG.colors.current)
        .attr("stroke-width", 2)
        .attr("d", line);

      // Add x-axis
      svg
        .append("g")
        .attr("class", "axis axis--x")
        .attr("transform", `translate(0,${height})`)
        .call(
          d3
            .axisBottom(xScale)
            .ticks(d3.utcDay.every(1)) // One tick per day
            .tickSize(0)
            .tickPadding(9)
            .tickFormat((d) => fmtDateLabel(d.toISOString())),
        )
        .call((g) => g.select(".domain").remove())
        .call((g) =>
          g
            .selectAll(".tick text")
            .style("color", "#9CA3AF")
            .style("font-size", "12px")
            .style("font-family", "Inter")
            .style("font-weight", "500"),
        );

      // Add tooltip functionality
      this.addTooltipLayer(
        svg,
        xScale,
        yScale,
        values,
        comparisonValues,
        width,
        height,
      );

      this.positionTodayMarker(elToday, data, containerWidth);
    },

    prepareChartData(data) {
      const mapValues = (vals) =>
        vals?.map((v) => ({
          date: v.date,
          label: fmtDateLabel(v.date),
          value: v.value,
        })) || [];

      const currentValues = mapValues(data.current.values);
      const values = this.fillDateRange(
        currentValues,
        data.current.start_date,
        data.today, // Use actual today's date, not feedback deadline
      );
      const comparisonValues = mapValues(data.previous?.values);

      return { values, comparisonValues };
    },

    fillDateRange(currentValues, startDate, endDate) {
      const valuesByDate = Object.fromEntries(
        currentValues.map((v) => [v.date, v.value]),
      );

      const result = [];
      const current = new Date(startDate + "T00:00:00Z");
      const end = new Date(endDate + "T00:00:00Z");
      let lastValue = null;

      while (current <= end) {
        const dateStr = current.toISOString().split("T")[0];
        // Use actual value if available, otherwise use previous day's value (cumulative)
        const value = valuesByDate[dateStr] ?? lastValue;
        result.push({
          date: dateStr,
          label: fmtDateLabel(dateStr),
          value: value,
        });
        lastValue = value;
        current.setUTCDate(current.getUTCDate() + 1);
      }

      return result;
    },

    addTooltipLayer(
      svg,
      xScale,
      yScale,
      values,
      comparisonValues,
      width,
      height,
    ) {
      const bisect = d3.bisector((d) => parseDate(d.date)).left;

      // Create tooltip as HTML element outside SVG
      this.tooltip?.remove?.();
      this.tooltip = null;

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "chart-tooltip")
        .style("position", "absolute")
        .style("visibility", "hidden")
        .style("pointer-events", "none")
        .style("z-index", "10000");
      this.tooltip = tooltip;

      // Focus elements for visual feedback
      const focus = svg
        .append("g")
        .attr("class", "focus")
        .style("display", "none");

      focus
        .append("circle")
        .attr("r", 4)
        .attr("fill", CHART_CONFIG.colors.current);

      // Tooltip overlay
      svg
        .append("rect")
        .attr("class", "overlay")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "none")
        .attr("pointer-events", "all")
        .on("mouseover", () => {
          focus.style("display", null);
          focus.raise();
          tooltip.style("visibility", "visible");
        })
        .on("mouseout", () => {
          focus.style("display", "none");
          tooltip.style("visibility", "hidden");
        })
        .on("mousemove", (event) => {
          const [mouseX] = d3.pointer(event);
          const x0 = xScale.invert(mouseX);
          let i = bisect(values, x0);

          // Find the closest point by comparing with the previous point
          if (i > 0 && i < values.length) {
            const d0 = values[i - 1];
            const d1 = values[i];
            i = x0 - parseDate(d0.date) > parseDate(d1.date) - x0 ? i : i - 1;
          } else if (i >= values.length) {
            i = values.length - 1;
          }

          const d = values[i];

          if (d && d.value !== null) {
            const xPos = xScale(parseDate(d.date));
            const yPos = yScale(d.value);

            focus.attr("transform", `translate(${xPos},${yPos})`);
            focus.raise();

            // Create tooltip HTML
            const params = {
              heading: "Participation",
              d: {
                label: d.label,
                value: d.value,
                formattedValue: fmtPct(d.value),
              },
            };

            const cd = comparisonValues?.[i];
            if (cd && cd.value !== null) {
              params.cd = {
                label: "Previous",
                value: cd.value,
                formattedValue: fmtPct(cd.value),
              };
            }

            // Position tooltip near data point
            const svgNode = svg.node();
            const svgRect = svgNode.getBoundingClientRect();
            const margin = {
              top: CHART_CONFIG.topMargin,
              left: CHART_CONFIG.leftMargin,
            };

            tooltip.html(chartTooltipHtml.default(params));

            const tooltipWidth = tooltip.node().offsetWidth;
            tooltip
              .style(
                "left",
                `${svgRect.left + margin.left + xPos - tooltipWidth - 10}px`,
              )
              .style("top", `${svgRect.top + margin.top + yPos - 60}px`);
          }
        });
    },

    positionTodayMarker(elToday, data, containerWidth) {
      if (!elToday || !data.today) return;

      const startMs = new Date(data.current.start_date).getTime();
      const todayMs = new Date(data.today).getTime();
      const endMs = new Date(data.chart_end_date).getTime();

      // Calculate what percentage of the way through the chart range we are
      const totalRange = endMs - startMs;
      const daysSinceStart = todayMs - startMs;
      const pct =
        totalRange <= 0
          ? 0
          : Math.max(0, Math.min(1, daysSinceStart / totalRange));

      // Calculate position accounting for margins
      const totalWidth = containerWidth || 800;
      const chartWidth =
        totalWidth - CHART_CONFIG.leftMargin - CHART_CONFIG.rightMargin;

      // Position in pixels = left margin + (percentage * chart width)
      const positionPx = CHART_CONFIG.leftMargin + pct * chartWidth;
      const position = (positionPx / totalWidth) * 100;

      elToday.style.left = `${position}%`;
      elToday.classList.remove("hidden");
      elToday.classList.add("flex");
    },
  };
}
